<script setup lang="ts">
import { ref, onErrorCaptured } from 'vue'
import { useI18n } from '@/i18n'

interface Props {
  fallbackMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  fallbackMessage: 'Something went wrong. Please try again.',
})

const emit = defineEmits<{
  (e: 'error', error: Error): void
}>()
const { t } = useI18n()

const hasError = ref(false)
const errorMessage = ref('')
const errorStack = ref('')
const showDetails = ref(false)

onErrorCaptured((error: Error, _instance, info) => {
  hasError.value = true
  errorMessage.value = error.message || t(props.fallbackMessage)
  errorStack.value = error.stack || ''

  // Emit error for logging
  emit('error', error)

  console.error('ErrorBoundary caught:', error, info)

  // Prevent error from propagating
  return false
})

const retry = () => {
  hasError.value = false
  errorMessage.value = ''
  errorStack.value = ''
  showDetails.value = false
}
</script>

<template>
  <div v-if="hasError" class="bg-red-50 border border-red-200 rounded-lg p-6">
    <div class="flex items-start">
      <div class="flex-shrink-0">
        <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <div class="ml-4 flex-1">
        <h3 class="text-lg font-medium text-red-800">
          {{ t('Error Occurred') }}
        </h3>
        <p class="mt-1 text-sm text-red-600">
          {{ errorMessage }}
        </p>

        <div class="mt-4 flex space-x-3">
          <button
            @click="retry"
            class="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
          >
            {{ t('Try Again') }}
          </button>
          <button
            v-if="errorStack"
            @click="showDetails = !showDetails"
            class="px-4 py-2 bg-white text-red-600 text-sm font-medium rounded-md border border-red-300 hover:bg-red-50 transition-colors"
          >
            {{ showDetails ? t('Hide Details') : t('Show Details') }}
          </button>
        </div>

        <Transition
          enter-active-class="transition ease-out duration-200"
          enter-from-class="opacity-0 -translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition ease-in duration-150"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 -translate-y-2"
        >
          <pre
            v-if="showDetails && errorStack"
            class="mt-4 p-3 bg-red-100 rounded text-xs text-red-800 overflow-x-auto"
          >{{ errorStack }}</pre>
        </Transition>
      </div>
    </div>
  </div>
  <slot v-else />
</template>
